import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, BarChart2, RefreshCw, CheckCircle, Zap, ChevronDown, ChevronUp, Lightbulb, AlertTriangle, Trophy, ClipboardList, Search, Rocket, MapPin } from "lucide-react";
import { T } from "./theme";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import Card from "./components/Card";
import Badge from "./components/Badge";
import Button from "./components/Button";
import Input from "./components/Input";
import LoadingSpinner from "./components/LoadingSpinner";

async function callGemini(system, user, max_tokens=8192) {
  const res = await fetch("/api/gemini", { method:"POST", headers:{"Content-Type":"application/json"}, credentials:"include", body:JSON.stringify({system,user,max_tokens}) });
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data.text||"";
}

const PERIODOS = [
  { key: "semana", label: "Semana" },
  { key: "mes", label: "Mês" },
  { key: "semestre", label: "Semestre" },
  { key: "ano", label: "Ano" },
  { key: "todos", label: "Todos" },
];

// ─── Ranking de Queixas (usa /api/rankings com cache) ─────────────────

function RankingQueixas({ onSelectQueixa }) {
  const [periodo, setPeriodo] = useState("mes");
  const [loading, setLoading] = useState(false);
  const [ranking, setRanking] = useState(null);
  const [erro, setErro] = useState(null);

  const lastCallRef = useRef(0);

  const carregar = async (p) => {
    // Proteção contra chamadas excessivas (mín 5s entre chamadas)
    const now = Date.now();
    if (now - lastCallRef.current < 5000) {
      setErro("Aguarde alguns segundos antes de tentar novamente.");
      return;
    }
    lastCallRef.current = now;

    setLoading(true); setErro(null);
    try {
      const r = await fetch(`/api/rankings?periodo=${p}`, { method: "POST", credentials: "include" });
      if (r.status === 429) { setErro("Limite de requisições atingido. Aguarde 1 minuto."); setLoading(false); return; }
      if (!r.ok) throw new Error(`Erro ${r.status}`);
      const data = await r.json();
      // Classificar automaticamente a área funcional pelo nome do cluster
      const classifyArea = (cat) => {
        const c = (cat || "").toLowerCase();
        if (/enferm|técnic|equipe|profission/.test(c)) return "EQUIPE ASSISTENCIAL";
        if (/médic|doutor|clínic/.test(c)) return "CORPO CLÍNICO";
        if (/esper|demor|agend|fila|triag/.test(c)) return "FLUXO OPERACIONAL";
        if (/recep|admiss|entrada|cadastr/.test(c)) return "RECEPÇÃO";
        if (/aliment|nutri|comida|refei/.test(c)) return "HOTELARIA HOSPITALAR";
        if (/limp|higien|quart|leito|infra/.test(c)) return "INFRAESTRUTURA";
        if (/exam|result|labor|diagnóst/.test(c)) return "DIAGNÓSTICO";
        if (/comunic|inform|orient/.test(c)) return "COMUNICAÇÃO";
        if (/organiz|process|document|burocraci/.test(c)) return "GESTÃO OPERACIONAL";
        if (/alta|intern|cirurg|proced/.test(c)) return "PROCESSOS CLÍNICOS";
        return "EXPERIÊNCIA DO PACIENTE";
      };

      const queixas = (data.reclamacoes || []).map((item, i) => ({
        id: i + 1,
        titulo: item.categoria,
        descricao: `${item.count} depoimentos (${item.pct}% das reclamações)`,
        frequencia: item.count,
        impacto: item.pct > 20 ? "alto" : item.pct > 10 ? "médio" : "baixo",
        area: classifyArea(item.categoria),
        exemplos: item.exemplos || [],
        oportunidade: `Representa ${item.pct}% das reclamações — ação prioritária recomendada`,
      }));
      setRanking(queixas);
    } catch (e) { setErro(e.message); }
    setLoading(false);
  };

  useEffect(() => { carregar(periodo); }, [periodo]);

  const iCor = { alto: T.red, "médio": T.amber, baixo: T.green };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: T.radiusSm, background: `${T.red}15`, border: `1px solid ${T.red}30`, display: "flex", alignItems: "center", justifyContent: "center" }}><BarChart2 size={16} color={T.red} /></div>
          <div><div style={{ fontSize: 15, fontWeight: 700, color: T.textHi, fontFamily: T.fontTitle }}>Ranking de Queixas</div><div style={{ fontSize: 12, color: T.textLow }}>Agrupamento semântico por IA (com cache)</div></div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <div style={{ display: "flex", gap: 4, background: T.bgInput, borderRadius: T.radiusSm, padding: 3, border: `1px solid ${T.border}` }}>
            {PERIODOS.map(p => (
              <button key={p.key} onClick={() => setPeriodo(p.key)} style={{
                padding: "5px 12px", borderRadius: T.radiusXs, fontSize: 12, cursor: "pointer",
                background: periodo === p.key ? T.bgCard : "transparent", border: "none",
                color: periodo === p.key ? T.textHi : T.textLow, fontWeight: periodo === p.key ? 700 : 500,
                boxShadow: periodo === p.key ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
                transition: T.transitionFast, fontFamily: T.fontBody,
              }}>{p.label}</button>
            ))}
          </div>
          <Button variant="secondary" size="sm" icon={RefreshCw} onClick={() => carregar(periodo)} />
        </div>
      </div>

      {loading && <div style={{ textAlign: "center", padding: "60px 20px" }}><LoadingSpinner label="Analisando padrões semanticamente..." /></div>}
      {erro && !loading && <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${T.red}10`, border: `1px solid ${T.red}30`, borderRadius: T.radiusSm, padding: 20, color: T.red, fontSize: 13 }}><AlertTriangle size={14} /> {erro}</div>}
      {ranking && ranking.length === 0 && !loading && <div style={{ textAlign: "center", padding: 60, color: T.textLow }}>Nenhum depoimento negativo no período.</div>}

      {ranking && ranking.length > 0 && !loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {ranking.map((q, i) => (
            <Card key={i} hover padding="18px 20px">
              <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                <div style={{ width: 36, height: 36, borderRadius: T.radiusSm, background: `${iCor[q.impacto]}15`, border: `1px solid ${iCor[q.impacto]}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 14, fontWeight: 800, color: iCor[q.impacto], fontFamily: T.fontTitle }}>{i + 1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: T.textHi }}>{q.titulo}</span>
                    <Badge color={iCor[q.impacto]}>{q.impacto.toUpperCase()}</Badge>
                    <Badge color={T.accent}>{q.area || q.titulo}</Badge>
                    <span style={{ fontSize: 11, color: T.textLow, marginLeft: "auto" }}>{q.frequencia} depoimentos</span>
                  </div>
                  <p style={{ margin: "0 0 8px", fontSize: 13, color: T.textMid, lineHeight: 1.6 }}>{q.descricao}</p>
                  <div style={{ height: 3, background: T.bgBase, borderRadius: 2, marginBottom: 10, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${Math.min((q.frequencia / (ranking[0]?.frequencia || 1)) * 100, 100)}%`, background: iCor[q.impacto], borderRadius: 2 }} />
                  </div>
                  {q.exemplos?.length > 0 && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
                      {q.exemplos.slice(0, 2).map((ex, j) => (
                        <div key={j} style={{ borderLeft: `2px solid ${T.red}40`, paddingLeft: 10, fontSize: 12, color: T.textMid, fontStyle: "italic", lineHeight: 1.5 }}>"{ex}"</div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 6, padding: "8px 12px", background: `${T.teal}08`, border: `1px solid ${T.teal}20`, borderRadius: T.radiusSm }}>
                    <Zap size={12} color={T.teal} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span style={{ fontSize: 12, color: T.teal, lineHeight: 1.5 }}>{q.oportunidade}</span>
                  </div>
                </div>
                <Button variant="secondary" size="sm" icon={Sparkles} onClick={() => onSelectQueixa(q)} style={{ flexShrink: 0, color: T.accent, borderColor: `${T.accent}40` }}>
                  Criar PDSA
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Wizard PDSA (Gemini direto — sob demanda) ─────────────────────────

function WizardPDSA({ queixaInicial, onCicloGerado }) {
  const [queixa, setQueixa] = useState(queixaInicial?.titulo || "");
  const [area, setArea] = useState(queixaInicial?.area || "");
  const [loading, setLoading] = useState(false);
  const [sugestao, setSugestao] = useState(null);
  const [erro, setErro] = useState(null);
  const [step, setStep] = useState(queixaInicial ? 2 : 1);

  useEffect(() => {
    if (queixaInicial) { setQueixa(queixaInicial.titulo); setArea(queixaInicial.area); gerarPDSA(queixaInicial); }
  }, [queixaInicial]);

  const gerarPDSA = async (q) => {
    setLoading(true); setErro(null); setSugestao(null); setStep(2);
    try {
      const resp = await callGemini(
        `Você é VP de Qualidade da Instituição ${window.usuarioUnidade || "Hospitalar"}. Responda APENAS em JSON válido.`,
        `Elabore ciclo PDSA completo para: "${q?.titulo || queixa}" / Área: ${q?.area || area}\nRetorne JSON:\n{"titulo":"título executivo","meta":"meta SMART","urgencia":"imediata"|"30 dias"|"90 dias","roi_estimado":"+X pontos NPS","plan":{"hipotese":"hipótese","acoes":["ação1","ação2","ação3"],"recursos":"recursos","responsavel":"cargo"},"do":{"execucao":"como executar","piloto":"piloto","prazo":"prazo"},"study":{"metricas":["métrica1","métrica2"],"frequencia_medicao":"como medir","criterio_sucesso":"sucesso"},"act":{"se_sucesso":"o que fazer","se_falha":"plano B","proximo_ciclo":"próximo"},"benchmarks":["b1","b2"],"riscos":["risco1","risco2"]}`,
        8192
      );
      setSugestao(JSON.parse(resp.replace(/```json|```/g, "").trim()));
    } catch (e) { setErro("Erro ao gerar PDSA."); }
    setLoading(false);
  };

  const uCor = { "imediata": T.red, "30 dias": T.amber, "90 dias": T.green };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <div style={{ width: 36, height: 36, borderRadius: T.radiusSm, background: `${T.purple}15`, border: `1px solid ${T.purple}30`, display: "flex", alignItems: "center", justifyContent: "center" }}><Sparkles size={16} color={T.purple} /></div>
        <div><div style={{ fontSize: 15, fontWeight: 700, color: T.textHi, fontFamily: T.fontTitle }}>Wizard PDSA com IA</div><div style={{ fontSize: 12, color: T.textLow }}>IA como Diretora de Qualidade</div></div>
      </div>

      {step === 1 && (
        <Card>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.textMid, marginBottom: 16 }}>Descreva a queixa ou problema a resolver:</div>
          <Input textarea value={queixa} onChange={e => setQueixa(e.target.value)} placeholder="Ex: Tempo de espera excessivo no PA..." />
          <div style={{ display: "flex", gap: 10, marginTop: 12, alignItems: "center" }}>
            <Input value={area} onChange={e => setArea(e.target.value)} placeholder="Área (ex: PA, Exames...)" />
            <Button icon={Sparkles} onClick={() => gerarPDSA(null)} disabled={!queixa.trim()}>Gerar PDSA</Button>
          </div>
        </Card>
      )}

      {loading && <div style={{ textAlign: "center", padding: "60px 20px" }}><LoadingSpinner label="Elaborando ciclo PDSA..." /></div>}
      {erro && <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${T.red}10`, border: `1px solid ${T.red}30`, borderRadius: T.radiusSm, padding: 20, color: T.red, fontSize: 13 }}><AlertTriangle size={14} /> {erro}</div>}

      {sugestao && !loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card style={{ borderColor: T.purple, boxShadow: `0 0 20px ${T.purple}10` }} padding="20px 24px">
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <Badge color={uCor[sugestao.urgencia] || T.amber} dot>{sugestao.urgencia}</Badge>
                  <Badge color={T.green}>ROI: {sugestao.roi_estimado}</Badge>
                </div>
                <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 800, color: T.textHi, fontFamily: T.fontTitle }}>{sugestao.titulo}</h3>
                <p style={{ margin: 0, fontSize: 13, color: T.textMid, lineHeight: 1.6 }}><strong style={{ color: T.textLow }}>Meta:</strong> {sugestao.meta}</p>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Button variant="secondary" size="sm" onClick={() => { setStep(1); setSugestao(null); }}>Refazer</Button>
                <Button size="sm" icon={CheckCircle} onClick={() => onCicloGerado(sugestao, area)}>Salvar Ciclo</Button>
              </div>
            </div>
          </Card>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 12 }}>
            {[{ key: "plan", label: "PLAN", cor: T.accent, campos: ["hipotese", "acoes", "recursos", "responsavel"] }, { key: "do", label: "DO", cor: T.amber, campos: ["execucao", "piloto", "prazo"] }, { key: "study", label: "STUDY", cor: T.purple, campos: ["metricas", "frequencia_medicao", "criterio_sucesso"] }, { key: "act", label: "ACT", cor: T.green, campos: ["se_sucesso", "se_falha", "proximo_ciclo"] }].map(({ key, label, cor, campos }) => (
              <Card key={key} padding="18px" style={{ borderTop: `3px solid ${cor}` }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: cor, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14, fontFamily: T.fontTitle }}>{label}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {campos.map(campo => {
                    const val = sugestao[key]?.[campo];
                    if (!val) return null;
                    return (
                      <div key={campo}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: T.textLow, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>{campo.replace(/_/g, " ")}</div>
                        {Array.isArray(val) ? <ul style={{ margin: 0, paddingLeft: 16 }}>{val.map((v, i) => <li key={i} style={{ fontSize: 12, color: T.textMid, lineHeight: 1.6, marginBottom: 2 }}>{v}</li>)}</ul> : <p style={{ margin: 0, fontSize: 12, color: T.textMid, lineHeight: 1.6 }}>{val}</p>}
                      </div>
                    );
                  })}
                </div>
              </Card>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Card padding="16px">
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: T.teal, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}><Trophy size={12} /> Benchmarks</div>
              {(sugestao.benchmarks || []).map((b, i) => <div key={i} style={{ fontSize: 12, color: T.textMid, lineHeight: 1.6, marginBottom: 6, paddingLeft: 10, borderLeft: `2px solid ${T.teal}40` }}>{b}</div>)}
            </Card>
            <Card padding="16px">
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: T.amber, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}><AlertTriangle size={12} /> Riscos</div>
              {(sugestao.riscos || []).map((r, i) => <div key={i} style={{ fontSize: 12, color: T.textMid, lineHeight: 1.6, marginBottom: 6, paddingLeft: 10, borderLeft: `2px solid ${T.amber}40` }}>{r}</div>)}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TIPs (usa /api/tips com cache) ──────────────────────────────────

function TIPs() {
  const [periodo, setPeriodo] = useState("mes");
  const [loading, setLoading] = useState(false);
  const [tips, setTips] = useState(null);
  const [erro, setErro] = useState(null);
  const [expandido, setExpandido] = useState(null);

  const lastCallRef = useRef(0);

  const carregar = async (p, forceSkipThrottle = false) => {
    const now = Date.now();
    if (!forceSkipThrottle && now - lastCallRef.current < 5000) {
      setErro("Aguarde alguns segundos antes de tentar novamente.");
      return;
    }
    lastCallRef.current = now;

    setLoading(true); setErro(null);
    try {
      const r = await fetch(`/api/tips?periodo=${p}`, { method: "POST", credentials: "include" });
      if (r.status === 429) { setErro("Limite de requisições atingido. Aguarde 1 minuto."); setLoading(false); return; }
      if (!r.ok) throw new Error(`Erro ${r.status}`);
      const data = await r.json();
      // Converter formato do tips
      const converted = {
        resumo_executivo: `Análise de ${data.tips?.reduce((s, t) => s + t.count, 0) || 0} depoimentos identificou ${data.tips?.length || 0} oportunidades de melhoria.`,
        score_nps_risco: data.tips?.length > 5 ? "Alto" : data.tips?.length > 3 ? "Médio" : "Baixo",
        tips: (data.tips || []).map((tip, i) => {
          const rec = tip.recomendacoes?.[0] || {};
          return {
            id: i + 1,
            titulo: tip.categoria,
            categoria: rec.responsavel || "Processo",
            prioridade: rec.impacto === "Alto" ? "crítica" : rec.impacto === "Baixo" ? "média" : "alta",
            insight: `${tip.count} depoimentos (${tip.pct}% do total)`,
            quick_win: rec.acao || "—",
            transformacao: rec.indicador || "Implementar melhoria contínua",
            impacto_nps: rec.impacto || "Médio",
            esforco: rec.prazo === "Imediato" ? "baixo" : rec.prazo === "Curto" ? "médio" : "alto",
            prazo_resultado: rec.prazo || "Curto",
            exemplos: tip.exemplos || [],
          };
        }),
        padrao_oculto: null,
        proximos_passos: (data.tips || []).slice(0, 3).map(t => t.recomendacoes?.[0]?.acao || "").filter(Boolean),
      };
      setTips(converted);
    } catch (e) { setErro(e.message); }
    setLoading(false);
  };

  useEffect(() => { carregar(periodo, true); }, [periodo]);

  const pCor = { "crítica": T.red, alta: T.amber, "média": T.teal };
  const cCor = { Processo: T.accent, "Comunicação": T.purple, Infraestrutura: T.amber, Cultura: T.green, Digital: T.teal, Treinamento: "#fb923c", Qualidade: T.purple, Enfermagem: T.green, Recepção: T.amber };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: T.radiusSm, background: `${T.teal}15`, border: `1px solid ${T.teal}30`, display: "flex", alignItems: "center", justifyContent: "center" }}><Lightbulb size={16} color={T.teal} /></div>
          <div><div style={{ fontSize: 15, fontWeight: 700, color: T.textHi, fontFamily: T.fontTitle }}>TIPs — Oportunidades de Melhoria</div><div style={{ fontSize: 12, color: T.textLow }}>Insights estratégicos com cache</div></div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <div style={{ display: "flex", gap: 4, background: T.bgInput, borderRadius: T.radiusSm, padding: 3, border: `1px solid ${T.border}` }}>
            {PERIODOS.map(p => (
              <button key={p.key} onClick={() => setPeriodo(p.key)} style={{
                padding: "5px 12px", borderRadius: T.radiusXs, fontSize: 12, cursor: "pointer",
                background: periodo === p.key ? T.bgCard : "transparent", border: "none",
                color: periodo === p.key ? T.textHi : T.textLow, fontWeight: periodo === p.key ? 700 : 500,
                boxShadow: periodo === p.key ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
                transition: T.transitionFast, fontFamily: T.fontBody,
              }}>{p.label}</button>
            ))}
          </div>
          <Button variant="secondary" size="sm" icon={RefreshCw} onClick={() => carregar(periodo)} />
        </div>
      </div>

      {loading && <div style={{ textAlign: "center", padding: "60px 20px" }}><LoadingSpinner label="Gerando insights..." /></div>}
      {erro && !loading && <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${T.red}10`, border: `1px solid ${T.red}30`, borderRadius: T.radiusSm, padding: 20, color: T.red, fontSize: 13 }}><AlertTriangle size={14} /> {erro}</div>}

      {tips && !loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card style={{ borderColor: T.teal, boxShadow: `0 0 20px ${T.teal}10` }} padding="20px 24px">
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, fontWeight: 700, color: T.teal, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}><ClipboardList size={11} /> Resumo Executivo</div>
            <p style={{ margin: "0 0 10px", fontSize: 13, color: T.textMid, lineHeight: 1.7 }}>{tips.resumo_executivo}</p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: tips.padrao_oculto ? 12 : 0 }}>
              <Badge color={T.red}>Risco NPS: {tips.score_nps_risco}</Badge>
              <Badge color={T.teal}>{tips.tips?.length || 0} oportunidades</Badge>
            </div>
            {tips.padrao_oculto && <div style={{ padding: "12px 16px", background: `${T.purple}08`, border: `1px solid ${T.purple}20`, borderRadius: T.radiusSm }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, fontWeight: 700, color: T.purple, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}><Search size={11} /> Padrão Não Óbvio</div>
              <p style={{ margin: 0, fontSize: 12, color: T.textMid, lineHeight: 1.6 }}>{tips.padrao_oculto}</p>
            </div>}
          </Card>

          {(tips.tips || []).map((tip, i) => (
            <Card key={i} noPadding>
              <div style={{ padding: "16px 20px", cursor: "pointer", display: "flex", alignItems: "flex-start", gap: 14 }} onClick={() => setExpandido(expandido === i ? null : i)}>
                <div style={{ width: 38, height: 38, borderRadius: T.radiusSm, background: `${pCor[tip.prioridade] || T.amber}15`, border: `1px solid ${pCor[tip.prioridade] || T.amber}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 15, fontWeight: 800, color: pCor[tip.prioridade] || T.amber, fontFamily: T.fontTitle }}>{i + 1}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: T.textHi }}>{tip.titulo}</span>
                    <Badge color={pCor[tip.prioridade] || T.amber}>{tip.prioridade}</Badge>
                    <Badge color={cCor[tip.categoria] || T.accent}>{tip.categoria}</Badge>
                    <span style={{ marginLeft: "auto", fontSize: 11, color: T.green, fontWeight: 600 }}>{tip.impacto_nps}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: T.textMid, lineHeight: 1.6 }}>{tip.insight}</p>
                </div>
                <div style={{ color: T.textLow, flexShrink: 0, marginTop: 4 }}>{expandido === i ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</div>
              </div>
              {expandido === i && (
                <div style={{ padding: "0 20px 20px", borderTop: `1px solid ${T.border}` }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16 }}>
                    <div style={{ padding: "14px 16px", background: `${T.green}08`, border: `1px solid ${T.green}20`, borderRadius: T.radiusSm }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, fontWeight: 700, color: T.green, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}><Zap size={10} /> Quick Win</div>
                      <p style={{ margin: 0, fontSize: 12, color: T.textMid, lineHeight: 1.6 }}>{tip.quick_win}</p>
                    </div>
                    <div style={{ padding: "14px 16px", background: `${T.accent}08`, border: `1px solid ${T.accent}20`, borderRadius: T.radiusSm }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, fontWeight: 700, color: T.accent, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}><Rocket size={11} /> Transformação</div>
                      <p style={{ margin: 0, fontSize: 12, color: T.textMid, lineHeight: 1.6 }}>{tip.transformacao}</p>
                    </div>
                  </div>
                  {tip.exemplos?.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: T.textLow, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Exemplos</div>
                      {tip.exemplos.slice(0, 2).map((ex, j) => (
                        <div key={j} style={{ borderLeft: `2px solid ${T.accent}40`, paddingLeft: 10, fontSize: 12, color: T.textMid, fontStyle: "italic", lineHeight: 1.5, marginBottom: 4 }}>"{ex}"</div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, color: T.textLow, padding: "4px 10px", borderRadius: T.radiusXs, border: `1px solid ${T.border}` }}>Esforço: {tip.esforco}</span>
                    <span style={{ fontSize: 11, color: T.textLow, padding: "4px 10px", borderRadius: T.radiusXs, border: `1px solid ${T.border}` }}>Resultado em: {tip.prazo_resultado}</span>
                  </div>
                </div>
              )}
            </Card>
          ))}

          {tips.proximos_passos?.length > 0 && (
            <Card padding="16px 20px">
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: T.textLow, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}><MapPin size={12} /> Próximos Passos</div>
              {tips.proximos_passos.map((p, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 20, height: 20, borderRadius: "50%", background: `${T.accent}18`, border: `1px solid ${T.accent}40`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: T.accent, flexShrink: 0 }}>{i + 1}</div>
                  <span style={{ fontSize: 13, color: T.textMid, lineHeight: 1.5 }}>{p}</span>
                </div>
              ))}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────

export default function PDSAIntelligente() {
  const [tab, setTab] = useState("ranking");
  const [queixaSelecionada, setQueixaSelecionada] = useState(null);
  const navigate = useNavigate();

  const handleSelectQueixa = (q) => { setQueixaSelecionada(q); setTab("wizard"); };

  const handleCicloGerado = async (ciclo, area) => {
    try {
      const novoCiclo = {
        titulo: ciclo.titulo, area: area || ciclo.area || "Geral", meta: ciclo.meta,
        prioridade: ciclo.urgencia === "imediata" ? "alta" : ciclo.urgencia === "30 dias" ? "media" : "baixa",
        status: "planejando",
        prazo: ciclo.urgencia === "imediata" ? new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10) : ciclo.urgencia === "30 dias" ? new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10) : new Date(Date.now() + 90 * 864e5).toISOString().slice(0, 10),
        plan: ciclo.plan?.acoes?.join("\n") || ciclo.plan?.hipotese || "",
        doText: ciclo.do?.execucao || ciclo.do?.piloto || "",
        study: ciclo.study?.metricas?.join(", ") || ciclo.study?.frequencia_medicao || "",
        act: ciclo.act?.se_sucesso || "",
        roi_estimado: ciclo.roi_estimado, urgencia: ciclo.urgencia,
        benchmarks: ciclo.benchmarks || [], riscos: ciclo.riscos || [], _origem: "wizard",
      };
      const r = await fetch("/api/pdsa", { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(novoCiclo) });
      const d = await r.json();
      if (d.ok) { setQueixaSelecionada(null); navigate("/pdsa", { state: { cicloNovo: d.ciclo } }); }
    } catch (e) { alert("Erro ao salvar ciclo PDSA"); }
  };

  const TABS = [
    { id: "ranking", label: "Ranking de Queixas", cor: T.red },
    { id: "wizard", label: "Wizard PDSA", cor: T.purple },
    { id: "tips", label: "TIPs", cor: T.teal },
  ];

  return (
    <PageContainer>
      <PageHeader icon={Sparkles} title="Inteligência de Qualidade" description="Análise semântica · Wizard PDSA · Oportunidades CXO — dados reais">
        <Badge color={T.purple} dot>IA Ativa</Badge>
      </PageHeader>

      <div style={{ display: "flex", gap: 6, marginBottom: 24, borderBottom: `1px solid ${T.border}` }}>
        {TABS.map(({ id, label, cor }) => (
          <button key={id} onClick={() => setTab(id)} style={{
            padding: "10px 20px", borderRadius: `${T.radiusSm}px ${T.radiusSm}px 0 0`, fontSize: 13, fontWeight: 600, fontFamily: T.fontBody,
            background: tab === id ? `${cor}12` : "transparent", border: `1px solid ${tab === id ? cor : "transparent"}`,
            borderBottom: tab === id ? `1px solid ${T.bgCard}` : "none", color: tab === id ? cor : T.textLow,
            cursor: "pointer", marginBottom: -1, transition: T.transitionFast, display: "flex", alignItems: "center", gap: 7,
          }}>
            {label}{id === "wizard" && queixaSelecionada && <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.purple, display: "inline-block" }} />}
          </button>
        ))}
      </div>

      {tab === "ranking" && <RankingQueixas onSelectQueixa={handleSelectQueixa} />}
      {tab === "wizard" && <WizardPDSA queixaInicial={queixaSelecionada} onCicloGerado={handleCicloGerado} />}
      {tab === "tips" && <TIPs />}
    </PageContainer>
  );
}
